'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from 'react';
import type { HydrateBundleFlavor, HydrateBundleOffer, InsiderBundleSettings } from '@/lib/insider-bundle-config';
import { DEFAULT_INSIDER_BUNDLE_SETTINGS } from '@/lib/insider-bundle-config';
import type { SubscribeSaveConfig } from '@/lib/subscribe-save-config';
import { type BundleFrequencyOption, type BundlePricing, type BundleSlots, clearSlotAndCompact, computeBundlePricing, createEmptySlots, getBundleFrequencyOptions, getDefaultFrequencyOption } from './hydrate-bundle-math';
export interface HydrateBundleState {
  config: InsiderBundleSettings;
  flavors: HydrateBundleFlavor[];
  kitProduct: HydrateBundleOffer['kitProduct'] | null;
  byHandle: Map<string, HydrateBundleFlavor>;
  slots: BundleSlots;
  fillSlot: (handle: string) => void;
  clearSlot: (index: number) => void;
  remainingCount: number;
  hasUnavailableSelection: boolean;
  /** Whether the Subscribe & Save option is offered at all (plans on the kit product). */
  showSubscribe: boolean;
  subscribeSelected: boolean;
  setSubscribeSelected: (selected: boolean) => void;
  frequencyOptions: BundleFrequencyOption[];
  selectedFrequency: BundleFrequencyOption | null;
  setSelectedFrequencyPhrase: (phrase: string) => void;
  pricing: BundlePricing | null;
  canAdd: boolean;
}
const NO_FLAVORS: HydrateBundleFlavor[] = [];

/**
 * All Crew Pack purchase state, lifted to the buy box so the headline price
 * row can mirror the bundle totals while the bundle tab is active. Safe to
 * call with a null bundle (empty state, never rendered).
 */
export function useHydrateBundleState(bundle, subscribeSaveConfig) {
  const $ = _c(46);
  const config = bundle?.config ?? DEFAULT_INSIDER_BUNDLE_SETTINGS;
  const flavors = bundle?.flavors ?? NO_FLAVORS;
  const kitProduct = bundle?.kitProduct ?? null;
  let t0;
  if ($[0] !== config) {
    t0 = () => createEmptySlots(config);
    $[0] = config;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [slots, setSlots] = useState(t0);
  let t1;
  if ($[2] !== flavors) {
    t1 = new Map(flavors.map(_temp));
    $[2] = flavors;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const byHandle = t1;
  let t2;
  if ($[4] !== kitProduct) {
    t2 = getBundleFrequencyOptions(kitProduct);
    $[4] = kitProduct;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const frequencyOptions = t2;
  let t3;
  if ($[6] !== frequencyOptions || $[7] !== subscribeSaveConfig.enabled) {
    t3 = () => subscribeSaveConfig.enabled && frequencyOptions.length > 0;
    $[6] = frequencyOptions;
    $[7] = subscribeSaveConfig.enabled;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const [isSubscribeSelected, setSubscribeSelected] = useState(t3);
  let t4;
  if ($[9] !== frequencyOptions) {
    t4 = () => getDefaultFrequencyOption(frequencyOptions)?.phrase ?? null;
    $[9] = frequencyOptions;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const [selectedFrequencyPhrase, setSelectedFrequencyPhrase] = useState(t4);
  const showSubscribe = subscribeSaveConfig.enabled && frequencyOptions.length > 0;
  const subscribeSelected = showSubscribe && isSubscribeSelected;
  let t5;
  if ($[11] !== frequencyOptions || $[12] !== selectedFrequencyPhrase || $[13] !== subscribeSelected) {
    t5 = subscribeSelected ? frequencyOptions.find(option => option.phrase === selectedFrequencyPhrase) ?? getDefaultFrequencyOption(frequencyOptions) : null;
    $[11] = frequencyOptions;
    $[12] = selectedFrequencyPhrase;
    $[13] = subscribeSelected;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const selectedFrequency = t5;
  let t6;
  if ($[15] !== flavors || $[16] !== frequencyOptions || $[17] !== kitProduct || $[18] !== selectedFrequency || $[19] !== slots) {
    t6 = kitProduct ? computeBundlePricing(kitProduct, flavors, slots, selectedFrequency ?? getDefaultFrequencyOption(frequencyOptions)) : null;
    $[15] = flavors;
    $[16] = frequencyOptions;
    $[17] = kitProduct;
    $[18] = selectedFrequency;
    $[19] = slots;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const pricing = t6;
  let t7;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = handle => {
      setSlots(prev => {
        const nextIndex = prev.indexOf(null);
        if (nextIndex === -1) {
          return prev;
        }
        const next = [...prev];
        next[nextIndex] = handle;
        return next;
      });
    };
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const fillSlot = t7;
  let t8;
  if ($[22] !== config) {
    t8 = index => {
      setSlots(prev_0 => clearSlotAndCompact(prev_0, index, config.boxCount));
    };
    $[22] = config;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const clearSlot = t8;
  let t9;
  if ($[24] !== slots) {
    t9 = slots.filter(_temp2);
    $[24] = slots;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const remainingCount = t9.length;
  let t10;
  if ($[26] !== byHandle || $[27] !== slots) {
    let t11;
    if ($[29] !== byHandle) {
      t11 = handle_0 => handle_0 && byHandle.get(handle_0)?.availableForSale === false;
      $[29] = byHandle;
      $[30] = t11;
    } else {
      t11 = $[30];
    }
    t10 = slots.some(t11);
    $[26] = byHandle;
    $[27] = slots;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const hasUnavailableSelection = t10;
  const canAdd = remainingCount === 0 && !hasUnavailableSelection && kitProduct?.availableForSale === true && (!subscribeSelected || selectedFrequency !== null);
  let t11;
  if ($[31] !== byHandle || $[32] !== canAdd || $[33] !== clearSlot || $[34] !== config || $[35] !== flavors || $[36] !== frequencyOptions || $[37] !== hasUnavailableSelection || $[38] !== kitProduct || $[39] !== pricing || $[40] !== remainingCount || $[41] !== selectedFrequency || $[42] !== showSubscribe || $[43] !== slots || $[44] !== subscribeSelected) {
    t11 = {
      config,
      flavors,
      kitProduct,
      byHandle,
      slots,
      fillSlot,
      clearSlot,
      remainingCount,
      hasUnavailableSelection,
      showSubscribe,
      subscribeSelected,
      setSubscribeSelected,
      frequencyOptions,
      selectedFrequency,
      setSelectedFrequencyPhrase,
      pricing,
      canAdd
    };
    $[31] = byHandle;
    $[32] = canAdd;
    $[33] = clearSlot;
    $[34] = config;
    $[35] = flavors;
    $[36] = frequencyOptions;
    $[37] = hasUnavailableSelection;
    $[38] = kitProduct;
    $[39] = pricing;
    $[40] = remainingCount;
    $[41] = selectedFrequency;
    $[42] = showSubscribe;
    $[43] = slots;
    $[44] = subscribeSelected;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  return t11;
}
function _temp2(slot) {
  return slot === null;
}
function _temp(flavor) {
  return [flavor.handle, flavor];
}